import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for Medical π
 * Implements Schema.org structured data for AI-powered search engines.
 * Optimized to outrank Pflegebox.de on Google, Bing, ChatGPT, Claude, Perplexity & Gemini.
 *
 * IMPORTANT: JSON-LD is rendered OUTSIDE Helmet via dangerouslySetInnerHTML
 * because Helmet has React 18+ compatibility issues with dynamic children.
 */

// Canonical Base URL - DO NOT change without updating sitemap.xml + robots.txt
const BASE_URL = 'https://medicalpi.de';

// Real company info - mirrors /impressum + Footer
const COMPANY_INFO = {
  name: 'Medical π',
  legalName: 'Josef F. und Benjamin A. GbR',
  alternateName: 'MedicalPi',
  description: 'Ihr Partner für kostenlose Pflegehilfsmittel mit Pflegegrad. Wir liefern monatlich zuzahlungsfreie Pflegehilfsmittel im Wert von bis zu 44 € direkt zu Ihnen nach Hause – vollständig von der Pflegekasse übernommen.',
  url: BASE_URL,
  logo: `${BASE_URL}/favicon.png`,
  image: `${BASE_URL}/og-image.png`,
  email: 'info@medicalpi.de',
  telephone: '+4952425835480',
  foundingDate: '2024',
  address: {
    streetAddress: 'Hauptstraße 67',
    addressLocality: 'Rheda-Wiedenbrück',
    postalCode: '33378',
    addressRegion: 'NRW',
    addressCountry: 'DE'
  },
  geo: {
    latitude: 51.8447,
    longitude: 8.3024
  },
  sameAs: [],
  priceRange: 'Kostenlos mit Pflegegrad',
  openingHours: 'Mo-Fr 08:00-20:00'
};

/**
 * JsonLd - Safe JSON-LD injection
 * Rendered outside Helmet with dangerouslySetInnerHTML
 */
export const JsonLd = ({ data }) => {
  if (!data) return null;
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
};

// ============================================================================
// Organization Schema (root org info)
// ============================================================================
export const OrganizationSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${BASE_URL}/#organization`,
    name: COMPANY_INFO.name,
    legalName: COMPANY_INFO.legalName,
    alternateName: COMPANY_INFO.alternateName,
    description: COMPANY_INFO.description,
    url: COMPANY_INFO.url,
    logo: {
      '@type': 'ImageObject',
      url: COMPANY_INFO.logo,
      width: 512,
      height: 512
    },
    image: COMPANY_INFO.image,
    email: COMPANY_INFO.email,
    foundingDate: COMPANY_INFO.foundingDate,
    address: {
      '@type': 'PostalAddress',
      ...COMPANY_INFO.address
    },
    areaServed: {
      '@type': 'Country',
      name: 'Germany'
    },
    knowsLanguage: ['de-DE'],
    slogan: 'Zuzahlungsfreie Pflegehilfsmittel – kostenlos nach Hause'
  };
  return <JsonLd data={schema} />;
};

// ============================================================================
// MedicalBusiness / LocalBusiness Schema
// ============================================================================
export const LocalBusinessSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalBusiness',
    '@id': `${BASE_URL}/#localbusiness`,
    name: COMPANY_INFO.name,
    description: COMPANY_INFO.description,
    url: COMPANY_INFO.url,
    image: COMPANY_INFO.image,
    email: COMPANY_INFO.email,
    address: {
      '@type': 'PostalAddress',
      ...COMPANY_INFO.address
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: COMPANY_INFO.geo.latitude,
      longitude: COMPANY_INFO.geo.longitude
    },
    areaServed: {
      '@type': 'Country',
      name: 'Germany'
    },
    priceRange: COMPANY_INFO.priceRange,
    medicalSpecialty: 'Geriatric',
    paymentAccepted: 'Krankenkasse, Sachleistung gemäß § 40 SGB XI',
    currenciesAccepted: 'EUR',
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '20:00'
    }
  };
  return <JsonLd data={schema} />;
};

// ============================================================================
// Website Schema with Search Action (Sitelinks Search Box)
// ============================================================================
export const WebsiteSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${BASE_URL}/#website`,
    url: BASE_URL,
    name: 'Medical π – Kostenlose Pflegehilfsmittel mit Pflegegrad',
    alternateName: 'Medical Pi',
    description: 'Die digitale Plattform für zuzahlungsfreie Pflegehilfsmittel in Deutschland. Bis zu 44 € monatlich – direkt von der Pflegekasse übernommen.',
    publisher: {
      '@id': `${BASE_URL}/#organization`
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/ratgeber?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    },
    inLanguage: 'de-DE'
  };
  return <JsonLd data={schema} />;
};

// ============================================================================
// Service Schema (Pflegebox / Monatslieferung)
// ============================================================================
export const ServiceSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${BASE_URL}/#service`,
    name: 'Pflegebox – Monatliche Pflegehilfsmittel-Lieferung',
    description: 'Kostenlose monatliche Lieferung von zuzahlungsfreien Pflegehilfsmitteln im Wert von bis zu 44 € (gemäß § 40 Abs. 2 SGB XI, Produktgruppe 54). Die Kosten werden vollständig von der Pflegekasse übernommen.',
    provider: {
      '@id': `${BASE_URL}/#organization`
    },
    serviceType: 'Pflegehilfsmittel-Versorgung',
    category: 'Pflegehilfsmittel zum Verbrauch (Produktgruppe 54)',
    areaServed: {
      '@type': 'Country',
      name: 'Germany'
    },
    audience: {
      '@type': 'PeopleAudience',
      audienceType: 'Pflegebedürftige mit Pflegegrad 1–5',
      suggestedMinAge: 0,
      healthCondition: {
        '@type': 'MedicalCondition',
        name: 'Pflegebedürftigkeit'
      }
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Pflegebox Produkte',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Einmalhandschuhe (Nitril & Latex)' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Händedesinfektionsmittel' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Flächendesinfektion' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Bettschutzeinlagen' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Mundschutz / OP-Masken' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'FFP2-Masken' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Schutzschürzen' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Fingerlinge' } }
      ]
    },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'EUR',
      availability: 'https://schema.org/InStock',
      description: 'Vollständig kostenlos mit Pflegegrad – bis zu 44 € monatlich von der Pflegekasse übernommen. Keine Zuzahlung.',
      eligibleRegion: {
        '@type': 'Country',
        name: 'Germany'
      },
      eligibleCustomerType: 'Personen mit Pflegegrad 1–5 in häuslicher Pflege',
      seller: {
        '@id': `${BASE_URL}/#organization`
      }
    },
    termsOfService: `${BASE_URL}/agb`,
    serviceOutput: 'Monatliche Pflegebox direkt nach Hause'
  };
  return <JsonLd data={schema} />;
};

// ============================================================================
// FAQ Schema – CRITICAL for AI Search & Google Featured Snippets
// ============================================================================
export const FAQSchema = ({ faqs }) => {
  if (!faqs || faqs.length === 0) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
  return <JsonLd data={schema} />;
};

// ============================================================================
// Product Schema for Pflegehilfsmittel
// ============================================================================
export const ProductSchema = ({ product }) => {
  if (!product) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.image || `${BASE_URL}/products/${product.id}.jpg`,
    brand: { '@type': 'Brand', name: product.manufacturer || 'Medical π' },
    category: 'Pflegehilfsmittel zum Verbrauch',
    audience: {
      '@type': 'PeopleAudience',
      audienceType: 'Pflegebedürftige mit Pflegegrad'
    },
    offers: {
      '@type': 'Offer',
      url: `${BASE_URL}/antrag`,
      priceCurrency: 'EUR',
      price: '0',
      availability: 'https://schema.org/InStock',
      seller: { '@id': `${BASE_URL}/#organization` },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: { '@type': 'MonetaryAmount', value: 0, currency: 'EUR' },
        shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'DE' },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 2, unitCode: 'd' },
          transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 3, unitCode: 'd' }
        }
      }
    }
  };
  return <JsonLd data={schema} />;
};

// ============================================================================
// Article Schema for Blog/Ratgeber (with speakable for Voice Search)
// ============================================================================
export const ArticleSchema = ({ article }) => {
  if (!article) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${BASE_URL}/ratgeber/${article.slug}#article`,
    headline: article.title,
    description: article.excerpt,
    image: article.image || `${BASE_URL}/og-image.png`,
    datePublished: article.publishedDate,
    dateModified: article.modifiedDate || article.publishedDate,
    author: {
      '@type': 'Organization',
      '@id': `${BASE_URL}/#organization`,
      name: 'Medical π Redaktion'
    },
    publisher: { '@id': `${BASE_URL}/#organization` },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${BASE_URL}/ratgeber/${article.slug}`
    },
    articleSection: article.category,
    keywords: article.tags?.join(', '),
    inLanguage: 'de-DE',
    isAccessibleForFree: true,
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['article h1', 'article h2', 'article p']
    }
  };
  return <JsonLd data={schema} />;
};

// ============================================================================
// Breadcrumb Schema
// ============================================================================
export const BreadcrumbSchema = ({ items }) => {
  if (!items || items.length === 0) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url ? `${BASE_URL}${item.url}` : undefined
    }))
  };
  return <JsonLd data={schema} />;
};

// ============================================================================
// HowTo Schema - for Pflegegrad-Rechner, Antrag, etc.
// ============================================================================
export const HowToSchema = ({ title, description, steps, totalTime = 'PT5M' }) => {
  if (!steps || steps.length === 0) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: title,
    description: description,
    totalTime: totalTime,
    inLanguage: 'de-DE',
    tool: {
      '@type': 'HowToTool',
      name: 'Medical π Online-Rechner'
    },
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
      url: step.url ? `${BASE_URL}${step.url}` : undefined
    }))
  };
  return <JsonLd data={schema} />;
};

// ============================================================================
// AggregateRating Schema (use only with REAL reviews to avoid Google penalty)
// ============================================================================
export const AggregateRatingSchema = ({ ratingValue, reviewCount, itemReviewed = 'Medical π Pflegebox' }) => {
  if (!ratingValue || !reviewCount) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'AggregateRating',
    itemReviewed: { '@type': 'Service', name: itemReviewed },
    ratingValue: ratingValue,
    bestRating: '5',
    worstRating: '1',
    ratingCount: reviewCount,
    reviewCount: reviewCount
  };
  return <JsonLd data={schema} />;
};

// ============================================================================
// MedicalWebPage Schema (for medical/care content pages)
// ============================================================================
export const MedicalWebPageSchema = ({ title, description, url, medicalAudience = 'Patient' }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    name: title,
    description: description,
    url: url ? `${BASE_URL}${url}` : BASE_URL,
    audience: {
      '@type': 'MedicalAudience',
      audienceType: medicalAudience
    },
    medicalAudience: {
      '@type': 'MedicalAudience',
      audienceType: 'Patient'
    },
    about: {
      '@type': 'MedicalCondition',
      name: 'Pflegebedürftigkeit',
      possibleTreatment: {
        '@type': 'MedicalTherapy',
        name: 'Häusliche Pflege mit Pflegehilfsmitteln'
      }
    },
    inLanguage: 'de-DE',
    isAccessibleForFree: true,
    publisher: { '@id': `${BASE_URL}/#organization` }
  };
  return <JsonLd data={schema} />;
};

// ============================================================================
// SEOHead - Main Meta Tag Component (Per-Page)
// ============================================================================
export const SEOHead = ({
  title,
  description,
  canonical,
  image,
  type = 'website',
  noindex = false,
  keywords
}) => {
  const fullTitle = title ? `${title} | Medical π` : 'Medical π | Zuzahlungsfreie Pflegehilfsmittel mit Pflegegrad';
  const fullDescription = description || 'Kostenlose Pflegehilfsmittel mit Pflegegrad – monatlich bequem nach Hause. 100% von der Pflegekasse bezahlt. Jetzt Anspruch online prüfen!';
  const fullCanonical = canonical ? `${BASE_URL}${canonical}` : BASE_URL;
  const fullImage = image || `${BASE_URL}/og-image.png`;
  const robotsContent = noindex
    ? 'noindex, nofollow'
    : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  React.useEffect(() => {
    document.title = fullTitle;
  }, [fullTitle]);

  return (
    <Helmet>
      <meta name="description" content={fullDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullCanonical} />
      <html lang="de" />
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      <meta name="bingbot" content="index, follow" />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content="de_DE" />
      <meta property="og:site_name" content="Medical π" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={fullImage} />

      {/* Author / Publisher */}
      <meta name="author" content="Medical π" />
      <meta name="publisher" content="Medical π" />
      <meta name="geo.region" content="DE" />
      <meta name="geo.placename" content="Deutschland" />
    </Helmet>
  );
};

export default SEOHead;
